<template>
	<Pupup v-if="showPreview" @close="closePreview" :width="700" :class="{ 'pitch-to-collab-overlay': true, feature: true }" :title="previewTitle">
		<template v-slot:content>
			<BrandPanel :mini="false" :data="featureBrand" :class="{ feature: true }" type="feature" :hideButton="featureOnly" />
		</template>
		<template v-slot:footer-inner>
			<div class="modal-actions button-wrap full-width-mobile center">
				<span class="button flip-colours back no-margin" @click="backToEdit" v-html="backToEditLabel" />
				<span class="button flip-colours save-for-later no-margin" v-if="showSaveForLater" @click="saveForLater" v-html="'Save for later'" />
				<span class="button no-margin" v-if="showSendForApproval" @click="submitFeature" v-html="sendApprovalLabel" />
			</div>
		</template>
	</Pupup>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useVueInstance } from "../../composables/useVueInstance";
import ApiService from "../../services/api.service";
import Pupup from "./Pupup.vue";
import BrandPanel from "./PitchBrandPanel.vue";

export default {
	name: "BrandConnectPreview",
	components: { Pupup, BrandPanel },
	setup() {
		const { store, $toast } = useVueInstance();
		const route = useRoute();

		const brand = computed(() => store.state.general.brand);
		const previewSource = computed(() => {
			const routeName = route?.name ? String(route.name) : "";
			if (routeName.startsWith("pitch")) return "pitch";
			if (routeName.startsWith("dashboard")) return "dashboard";
			return "";
		});
		const subscription = computed(() => store.state.general.subscription || {});
		const featureOnly = computed(() => store.state.general.featureOnly);
		const draft = computed(() => store.state.general.brandConnectFeatureDraft);
		const showPreview = computed(() => !!draft.value);
		const paymentRedirect = "/payment-processing";

		const featureBrand = ref({
			pitch_title: "",
			email: "",
			firstName: "",
			surname: "",
			name: "",
			pitch_description: "",
			"brand-logo": "",
			"pitch-lifestyle": "",
			account_contact: true,
			lists: {
				want: "",
				bad: "",
				wants: [],
				bads: [],
			},
			pitch_id: null,
			pitch_comments: "",
			briefWriterRunId: null,
			editing: false,
		});

		const updateVar = (payload) => store.commit("general/updateVar", payload);
		const updateLoadingText = (payload) => store.commit("general/updateLoadingText", payload);
		const showSubscriptionPupup = (payload) => store.commit("general/showSubscriptionPupup", payload);

		const previewTitle = computed(() => "Preview your Opportunity");
		const featureAwaitingApproval = computed(() => !!(draft.value && draft.value.pending && !draft.value.approved));
		const featurePaid = computed(() => !!(draft.value && draft.value.paid) || !!subscription.value?.featureDone?.paid);
		const sendApprovalLabel = computed(() => {
			if (!featureOnly.value||featureBrand.value.freeOpportunity||featurePaid.value) return "Send for approval";
			return "Pay & send for approval";
		});
		const showSaveForLater = computed(() => !featureAwaitingApproval.value && !draft.value?.editing && !featureAwaitingApproval.value);
		const backToEditLabel = "Edit";

		const normalizeString = (value) => (typeof value === "string" ? value.trim() : "");
		const normalizeList = (value) => (Array.isArray(value) ? value.map((item) => normalizeString(item)) : []);
		const normalizeContact = (value) => {
			const contact = value && typeof value === "object" ? value : {};
			const email = normalizeString(contact.email);
			if (!email) return null;

			return {
				fname: normalizeString(contact.fname),
				sname: normalizeString(contact.sname),
				email,
			};
		};
		const normalizeFeatureData = (value) => {
			const data = value && typeof value === "object" ? value : {};
			return {
				title: normalizeString(data.title != null ? data.title : data.pitch_title),
				message: normalizeString(data.message != null ? data.message : data.pitch_description),
				wants: normalizeList(data.wants != null ? data.wants : data.lists?.wants),
				bads: normalizeList(data.bads != null ? data.bads : data.lists?.bads),
				lifestyleImage: normalizeString(data.lifestyleImage != null ? data.lifestyleImage : data["pitch-lifestyle"]),
				customContact: normalizeContact(data.customContact),
			};
		};
		const isEditingDraft = computed(() => !!(featureBrand.value?.editing || draft.value?.editing));
		const isNewDraft = computed(() => !isEditingDraft.value);
		const hasDraftChanges = computed(() => {
			if (!isEditingDraft.value) return false;
			if (!draft.value?.originalData) return false;

			return JSON.stringify(normalizeFeatureData(draft.value)) !== JSON.stringify(normalizeFeatureData(draft.value.originalData));
		});
		const showSendForApproval = computed(() => isNewDraft.value || hasDraftChanges.value || (draft.value && !draft.value.pending));

		const resetFeatureBrand = () => {
			featureBrand.value = {
				...featureBrand.value,
				pitch_title: "",
				pitch_description: "",
				"pitch-lifestyle": "",
				lists: { want: "", bad: "", wants: [], bads: [] },
				pitch_id: null,
				pitch_comments: "",
				briefWriterRunId: null,
				editing: false,
				account_contact: true,
			};
		};

		async function getUserInfo() {
			if (!brand.value) return;
			try {
				const response = await ApiService.get("pitch/user-info/" + brand.value);
				const userInfo = response.data;
				featureBrand.value.name = userInfo.name || "";
				featureBrand.value.firstName = userInfo.fname || "";
				featureBrand.value.surname = userInfo.sname || "";
				featureBrand.value.email = userInfo.email || "";
				featureBrand.value["brand-logo"] = userInfo.logo || "";
				featureBrand.value.freeOpportunity = !!userInfo.freeOpportunity;
			} catch (error) {
				console.error("Error fetching user info:", error);
			}
		}

		function applyDraft(featureData) {
			if (!featureData) return;
			featureBrand.value.pitch_title = featureData.title || "";
			featureBrand.value.pitch_description = featureData.message || "";
			featureBrand.value.lists.wants = Array.isArray(featureData.wants) ? featureData.wants : [];
			featureBrand.value.lists.bads = Array.isArray(featureData.bads) ? featureData.bads : [];
			featureBrand.value["pitch-lifestyle"] = featureData.lifestyleImage || "";
			featureBrand.value.editing = !!featureData.isEditing;
			featureBrand.value.briefWriterRunId = featureData.briefWriterRunId != null ? featureData.briefWriterRunId : null;

			const incomingPitchIdRaw =
				featureData && featureData.pitchId != null
					? featureData.pitchId
					: featureData && featureData.pitch_id != null
						? featureData.pitch_id
						: null;
			const incomingPitchIdParsed = incomingPitchIdRaw != null ? parseInt(incomingPitchIdRaw, 10) : null;
			if (Number.isFinite(incomingPitchIdParsed)) {
				featureBrand.value.pitch_id = incomingPitchIdParsed;
			}

			if (featureData.customContact && featureData.customContact.email) {
				featureBrand.value.account_contact = false;
				featureBrand.value.firstName = featureData.customContact.fname;
				featureBrand.value.surname = featureData.customContact.sname;
				featureBrand.value.email = featureData.customContact.email;
			} else {
				featureBrand.value.account_contact = true;
			}
		}

		watch(
			draft,
			(newDraft) => {
				if (!newDraft) return;
				applyDraft(newDraft);
				getUserInfo();
			},
			{ immediate: true }
		);

		watch(
			brand,
			(newValue) => {
				if (newValue && showPreview.value) {
					getUserInfo();
				}
			},
			{ immediate: true }
		);

		function closePreview() {
			updateVar({ varName: "brandConnectFeatureDraft", info: null });
		}

		function backToEdit() {
			const persistedData = draft.value?.persistedData || draft.value?.originalData || null;
			const initialData = {
				pitch_title: featureBrand.value.pitch_title || "",
				pitch_description: featureBrand.value.pitch_description || "",
				briefWriterRunId: featureBrand.value.briefWriterRunId || null,
				lists: {
					wants: Array.isArray(featureBrand.value.lists?.wants) ? featureBrand.value.lists.wants : [],
					bads: Array.isArray(featureBrand.value.lists?.bads) ? featureBrand.value.lists.bads : [],
				},
				"pitch-lifestyle": featureBrand.value["pitch-lifestyle"] || "",
				pitch_id: featureBrand.value.pitch_id || null,
				pending: !!draft.value?.pending,
				approved: !!draft.value?.approved,
				paid: !!draft.value?.paid,
				current: !!draft.value?.current,
				_id: draft.value?._id || featureBrand.value.pitch_id || null,
				account_contact: !!featureBrand.value.account_contact,
				firstName: featureBrand.value.firstName || "",
				surname: featureBrand.value.surname || "",
				email: featureBrand.value.email || "",
				originalData: persistedData,
				persistedData,
			};

			updateVar({ varName: "brandConnectFeatureDraft", info: null });
			updateVar({
				varName: "brandConnectModal",
				info: { show: true, connecteeId: null, initialData, source: previewSource.value },
			});
		}

		function saveForLater() {
			closePreview();
			$toast.success("Opportunity saved.");
		}

		function subscribe() {
			let ops = { paymentRedirect: paymentRedirect, title: "" };
			if (featureOnly.value) {
				ops.pitchId = featureBrand.value.pitch_id;
				ops.featureOnly = true;
			}
			showSubscriptionPupup(ops);
		}

		function validateFeature() {
			if (!featureBrand.value.pitch_title) {
				$toast.error("Please enter your title");
				return false;
			}
			if (!featureBrand.value.pitch_description) {
				$toast.error("Please enter some details about your idea");
				return false;
			}
			if (!featureBrand.value.lists.wants.length) {
				$toast.error("Please enter some bullet points about what you are looking for");
				return false;
			}
			if (!featureBrand.value["brand-logo"]) {
				if (!featureBrand.value["pitch-lifestyle"]) {
					$toast.error("Please add your brand logo and lifestyle image");
					return false;
				}
				$toast.error("Please add your brand logo");
				return false;
			}
			if (!featureBrand.value["pitch-lifestyle"]) {
				$toast.error("Please add your lifestyle image");
				return false;
			}
			if (!featureBrand.value.account_contact) {
				const requiredFields = ["firstName", "surname", "email"];
				const missing = requiredFields.some((field) => !featureBrand.value[field]);
				if (missing) {
					$toast.error("Please fill in all required fields");
					return false;
				}
			}
			return true;
		}

		async function submitFeature() {
			if (!validateFeature()) return;
			updateLoadingText(true);
			try {
				const response = await ApiService.post("pitch/feature", { ...featureBrand.value });
				updateVar({ varName: "subscription", info: response.data.subscription });

				const needsPayment = !!response.data.needsPayment;
				if (!needsPayment) {
					$toast.success("Thanks! You'll be notified by email once approved.");
				}

				store.commit("general/featureSubmit");
				closePreview();

				if (needsPayment) {
					subscribe();
				} else {
					resetFeatureBrand();
				}
			} catch (err) {
				console.error("Error submitting feature:", err);
				if (!!err.response && !!err.response.data && !!err.response.data.message) {
					$toast.error(err.response.data.message.message || "Failed to submit feature.");
				} else if (!!err.response && !!err.response.data && !!err.response.data.error) {
					$toast.error(err.response.data.error);
				} else {
					$toast.error("Failed to submit feature. Please try again.");
				}
			} finally {
				updateLoadingText();
			}
		}

		return {
			showPreview,
			previewTitle,
			featureOnly,
			featureBrand,
			backToEditLabel,
			sendApprovalLabel,
			showSaveForLater,
			showSendForApproval,
			closePreview,
			backToEdit,
			saveForLater,
			submitFeature,
			draft,
		};
	},
};
</script>
